import { noop, ServerError } from "@flaminem/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm, Control, UseFormReturn, UseFormGetValues } from "react-hook-form";
import { object, array, string } from "zod";

import { useGetBlacklistableIdentifiers } from "src/graphql/anti-fraud/queries/cases/GetCaseBlacklistableIdentifiersQuery";
import { mapToBlacklistableCaseIdentifier } from "src/mappers/case.mappers.ts";
import { BlacklistableCaseIdentifier } from "src/types/case";

export type BlacklistIdentifiersFormControl = {
  control: Control<BlacklistIdentifiersFormData>;
  submit: (
    onValid: (data: BlacklistIdentifiersFormData) => void
  ) => (e?: React.BaseSyntheticEvent) => Promise<void> | void;
  reset: () => void;
  hasError: boolean;
  methods: UseFormReturn<BlacklistIdentifiersFormData>;
  getValues: UseFormGetValues<BlacklistIdentifiersFormData>;
  blacklistableIdentifiers: {
    data: BlacklistableCaseIdentifier[];
    loading: boolean;
    error: ServerError | null;
  };
};

export type BlacklistIdentifiersFormData = {
  selectedIdentifiers: {
    id: string;
    identifierCode: string;
    elementCode: string;
    blacklistCode: string;
  }[];
};

export type UseBlacklistIdentifiersFormProps = {
  caseId: string;
};

export const useBlacklistIdentifiersForm = ({
  caseId,
}: UseBlacklistIdentifiersFormProps): BlacklistIdentifiersFormControl => {
  const validationSchema = useMemo(
    () =>
      object({
        selectedIdentifiers: array(
          object({
            id: string(),
            identifierCode: string(),
            elementCode: string(),
            blacklistCode: string(),
          })
        ),
      }),
    []
  );

  const methods = useForm<BlacklistIdentifiersFormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      selectedIdentifiers: [],
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitted },
    reset,
  } = methods;

  const {
    data: blacklistableIdentifiers,
    loading: loadingBlacklistableIdentifiers,
    error: errorBlacklistableIdentifiers,
  } = useGetBlacklistableIdentifiers({
    variables: {
      id: caseId,
      includeLinkedCaseIdentifiers: true,
    },
    onCompleted: (data) => {
      const identifiers =
        data.case?.blacklistableIdentifiers?.map(mapToBlacklistableCaseIdentifier) ?? [];

      reset({
        selectedIdentifiers: identifiers
          .filter((item) => item.belongsToCurrentCase)
          .map(({ id, identifier, element, blacklist }) => ({
            id,
            identifierCode: identifier.schemaIdentifier.code,
            elementCode: element.typeCode,
            blacklistCode: blacklist.code,
          })),
      });
    },
  });

  const submit = (onValid: (data: BlacklistIdentifiersFormData) => void | Promise<void>) => {
    if (loadingBlacklistableIdentifiers || errorBlacklistableIdentifiers) return noop;

    if (blacklistableIdentifiers.length === 0)
      return async () => onValid({ selectedIdentifiers: [] });

    return handleSubmit(onValid);
  };

  return {
    control,
    submit,
    reset: () => methods.reset({ selectedIdentifiers: [] }),
    hasError: isSubmitted ? !isValid : false,
    methods,
    blacklistableIdentifiers: {
      data: blacklistableIdentifiers,
      loading: loadingBlacklistableIdentifiers,
      error: errorBlacklistableIdentifiers,
    },
    getValues: methods.getValues,
  };
};
